import React from 'react';
import PropTypes from 'prop-types';
import { TablePagination } from '@mui/material';
import { alpha } from '@mui/material/styles';

const DEFAULT_PAGE_SIZES = [10, 25, 50, 100];

const getItemAriaLabel = (type) => {
  const labels = {
    first: 'Ir a la primera página',
    previous: 'Ir a la página anterior',
    next: 'Ir a la página siguiente',
    last: 'Ir a la última página',
  };
  return labels[type] || 'Cambiar página';
};

export default function AppPagination({
  count,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = DEFAULT_PAGE_SIZES,
  labelRowsPerPage = 'Filas por página',
  inTable = false,
  showFirstButton = true,
  showLastButton = true,
  sx,
}) {
  const rootProps = inTable ? {} : { component: 'div' };
  const normalizedCount = Number(count);

  return (
    <TablePagination
      {...rootProps}
      count={normalizedCount === -1 ? -1 : Math.max(0, normalizedCount || 0)}
      page={Math.max(0, Number(page) || 0)}
      rowsPerPage={rowsPerPage}
      onPageChange={onPageChange}
      onRowsPerPageChange={onRowsPerPageChange}
      rowsPerPageOptions={rowsPerPageOptions}
      labelRowsPerPage={labelRowsPerPage}
      labelDisplayedRows={({ from, to, count: total }) =>
        `${from}–${to} de ${total !== -1 ? total : `más de ${to}`}`
      }
      showFirstButton={showFirstButton}
      showLastButton={showLastButton}
      getItemAriaLabel={getItemAriaLabel}
      sx={[
        (theme) => ({
          width: '100%',
          minHeight: 62,
          borderTop: `1px solid ${theme.palette.divider}`,
          backgroundColor:
            theme.palette.mode === 'light'
              ? alpha(theme.palette.background.paper, 0.82)
              : alpha(theme.palette.background.paper, 0.58),
          '& .MuiTablePagination-toolbar': {
            minHeight: 62,
            px: { xs: 1, sm: 2 },
            gap: 0.5,
          },
          '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
            color: theme.palette.text.secondary,
            fontSize: '0.8125rem',
            fontWeight: 650,
          },
          '& .MuiTablePagination-select': {
            fontWeight: 750,
            color: theme.palette.text.primary,
          },
          '& .MuiTablePagination-actions': {
            ml: { xs: 0.25, sm: 1 },
            display: 'flex',
            gap: 0.25,
          },
          '& .MuiIconButton-root': {
            width: 32,
            height: 32,
            borderRadius: 1.25,
            '&:not(.Mui-disabled):hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
            },
          },
          [theme.breakpoints.down('sm')]: {
            '& .MuiTablePagination-toolbar': {
              justifyContent: 'center',
              flexWrap: 'wrap',
              py: 0.75,
            },
            '& .MuiTablePagination-spacer': { display: 'none' },
            '& .MuiTablePagination-selectLabel': { ml: 0 },
            '& .MuiTablePagination-displayedRows': { ml: 0.5 },
          },
        }),
        sx,
      ]}
    />
  );
}

AppPagination.propTypes = {
  count: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onRowsPerPageChange: PropTypes.func,
  rowsPerPageOptions: PropTypes.arrayOf(PropTypes.number),
  labelRowsPerPage: PropTypes.string,
  inTable: PropTypes.bool,
  showFirstButton: PropTypes.bool,
  showLastButton: PropTypes.bool,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.func]),
};
